import type { AgreementStatus } from "@app/types";

export type BaaSignerFlow = "self" | "external" | null;

export const baaStatusPollIntervalMs = 3000;
export const baaStatusPollMaxMs = 10 * 60 * 1000;
const baaExternalSignerStoragePrefix = "baa-external-signer";
const baaSelfSignerStoragePrefix = "baa-self-signer";
export const baaSelfSignerStateMaxAgeMs = 2 * 60 * 60 * 1000;

interface PersistedSelfSignerState {
  userId: string;
  createdAtMs: number;
  popupBlocked: boolean;
  agreementId?: string;
}

export function baaExternalSignerStorageKey(orgId: string): string {
  return `${baaExternalSignerStoragePrefix}:${orgId}`;
}

export function baaSelfSignerStorageKey(orgId: string, userId: string): string {
  return `${baaSelfSignerStoragePrefix}:${orgId}:${userId}`;
}

export function isTerminalBaaStatus(status: AgreementStatus): boolean {
  return status === "SIGNED" || status === "CANCELLED";
}

export function shouldResetSignerState(status: AgreementStatus): boolean {
  return status === "NONE" || isTerminalBaaStatus(status);
}

export function shouldPollBaaStatus(args: {
  status: AgreementStatus;
  signerFlow: BaaSignerFlow;
  isAwaitingSignedStatus: boolean;
  pollStartedAtMs: number | null;
  nowMs?: number;
  maxPollMs?: number;
}): boolean {
  const nowMs = args.nowMs ?? Date.now();
  const maxPollMs = args.maxPollMs ?? baaStatusPollMaxMs;
  if (args.status !== "OUT_FOR_SIGNATURE") return false;
  if (
    !args.isAwaitingSignedStatus &&
    args.signerFlow !== "external" &&
    args.signerFlow !== "self"
  ) {
    return false;
  }
  if (args.pollStartedAtMs == null) return true;
  return nowMs - args.pollStartedAtMs < maxPollMs;
}

export function shouldShowSelfSigningPending(args: {
  status: AgreementStatus;
  signingUrl?: string;
  signerFlow: BaaSignerFlow;
}): boolean {
  return (
    args.status === "OUT_FOR_SIGNATURE" &&
    !!args.signingUrl &&
    args.signerFlow === "self"
  );
}

export function readSelfSignerState(
  orgId: string,
  userId: string,
  options?: {
    storage?: Pick<Storage, "getItem"> | null;
    nowMs?: number;
    maxAgeMs?: number;
  },
): PersistedSelfSignerState | null {
  try {
    const targetStorage =
      options?.storage === undefined
        ? globalThis.sessionStorage
        : options.storage;
    const raw = targetStorage?.getItem(baaSelfSignerStorageKey(orgId, userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;
    const parsedUserId = obj.userId;
    const createdAtMs = obj.createdAtMs;
    const popupBlocked = obj.popupBlocked;
    const agreementId = obj.agreementId;
    if (typeof parsedUserId !== "string" || parsedUserId !== userId)
      return null;
    if (typeof createdAtMs !== "number" || !Number.isFinite(createdAtMs)) {
      return null;
    }
    if (typeof popupBlocked !== "boolean") return null;

    const nowMs = options?.nowMs ?? Date.now();
    const maxAgeMs = options?.maxAgeMs ?? baaSelfSignerStateMaxAgeMs;
    if (nowMs - createdAtMs > maxAgeMs) return null;

    return {
      userId: parsedUserId,
      createdAtMs,
      popupBlocked,
      agreementId: typeof agreementId === "string" ? agreementId : undefined,
    };
  } catch {
    return null;
  }
}

export function writeSelfSignerState(
  orgId: string,
  userId: string,
  state: {
    popupBlocked: boolean;
    agreementId?: string;
    createdAtMs?: number;
  },
  storage?: Pick<Storage, "setItem"> | null,
): void {
  try {
    const targetStorage =
      storage === undefined ? globalThis.sessionStorage : storage;
    const nextState: PersistedSelfSignerState = {
      userId,
      createdAtMs: state.createdAtMs ?? Date.now(),
      popupBlocked: state.popupBlocked,
      ...(state.agreementId ? { agreementId: state.agreementId } : {}),
    };
    targetStorage?.setItem(
      baaSelfSignerStorageKey(orgId, userId),
      JSON.stringify(nextState),
    );
  } catch {
    // ignore
  }
}

export function clearSelfSignerState(
  orgId: string,
  userId: string,
  storage?: Pick<Storage, "removeItem"> | null,
): void {
  try {
    const targetStorage =
      storage === undefined ? globalThis.sessionStorage : storage;
    targetStorage?.removeItem(baaSelfSignerStorageKey(orgId, userId));
  } catch {
    // ignore
  }
}

export function readExternalSignerEmail(
  orgId: string,
  storage?: Pick<Storage, "getItem"> | null,
): string | null {
  try {
    const targetStorage =
      storage === undefined ? globalThis.sessionStorage : storage;
    const raw = targetStorage?.getItem(baaExternalSignerStorageKey(orgId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const email = (parsed as Record<string, unknown>).email;
    return typeof email === "string" && email.trim() ? email.trim() : null;
  } catch {
    return null;
  }
}

export function writeExternalSignerEmail(
  orgId: string,
  email: string,
  storage?: Pick<Storage, "setItem"> | null,
): void {
  try {
    const targetStorage =
      storage === undefined ? globalThis.sessionStorage : storage;
    targetStorage?.setItem(
      baaExternalSignerStorageKey(orgId),
      JSON.stringify({ email: email.trim() }),
    );
  } catch {
    // ignore
  }
}

export function clearExternalSignerEmail(
  orgId: string,
  storage?: Pick<Storage, "removeItem"> | null,
): void {
  try {
    const targetStorage =
      storage === undefined ? globalThis.sessionStorage : storage;
    targetStorage?.removeItem(baaExternalSignerStorageKey(orgId));
  } catch {
    // ignore
  }
}
